import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import DOMPurify from 'dompurify'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, Clock, Loader2, ShieldAlert, TriangleAlert } from 'lucide-react'
import { getConsentLink, submitConsent, verifyDob } from '@/api/consent'
import { asConsentError } from '@/lib/apiClient'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { StatusCard } from '@/components/StatusCard'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/$token')({
  component: ConsentPage,
})

type Terminal = 'expired' | 'rate_limited' | 'locked' | 'error'

/** Map a thrown error to the consent API's uniform `{ status }` discriminator, falling back to HTTP. */
function outcomeOf(err: unknown): string {
  const e = asConsentError(err)
  if (!e) return 'error'
  if (e.code) return e.code
  if (e.status === 410) return 'expired'
  if (e.status === 429) return 'rate_limited'
  if (e.status === 409) return 'copy_updated'
  return 'error'
}

/** Outcomes possible on the initial link GET collapse to a terminal screen. */
function loadTerminal(err: unknown): Terminal {
  const o = outcomeOf(err)
  return o === 'expired' || o === 'rate_limited' ? o : 'error'
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Privacy-friendly YouTube embed URL from a watch/share URL or bare 11-char id; null if unparseable. */
function youTubeEmbedUrl(reference: string): string | null {
  if (!reference) return null
  const match = reference.match(
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/))([\w-]{11})/,
  )
  const id = match ? match[1] : /^[\w-]{11}$/.test(reference) ? reference : null
  return id ? `https://www.youtube-nocookie.com/embed/${id}` : null
}

function ConsentPage() {
  const { token } = Route.useParams()

  const [step, setStep] = useState<'verify' | 'review' | 'done'>('verify')
  const [terminal, setTerminal] = useState<Terminal | null>(null)
  const [verification, setVerification] = useState<{ patientName: string; verificationToken: string } | null>(null)
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null)
  const [reviewNotice, setReviewNotice] = useState<string | null>(null)

  // GET the link once. Every expected non-2xx is mapped to a screen, so suppress the global toast.
  const linkQuery = useQuery({
    queryKey: ['consent-link', token],
    queryFn: () => getConsentLink(token),
    retry: false,
    staleTime: Infinity,
    meta: { suppressToast: true },
  })

  const verifyMut = useMutation({
    mutationFn: (dateOfBirth: string) => verifyDob(token, { dateOfBirth }),
    meta: { suppressToast: true },
  })

  const submitMut = useMutation({
    mutationFn: (vars: { verificationToken: string; consentVersion: number | string }) =>
      submitConsent(token, { ...vars, accepted: true }),
    meta: { suppressToast: true },
  })

  async function handleVerify(dateOfBirth: string) {
    try {
      const result = await verifyMut.mutateAsync(dateOfBirth)
      setVerification(result)
      setVerifyNotice(null)
      setStep('review')
    } catch (err) {
      const outcome = outcomeOf(err)
      if (outcome === 'locked') setTerminal('locked')
      else if (outcome === 'rate_limited') setTerminal('rate_limited')
      else if (outcome === 'expired') setTerminal('expired')
      // 'mismatch' / generic are shown inline (derived from verifyMut.error below)
    }
  }

  async function handleSubmit() {
    if (!verification || !linkQuery.data) return
    try {
      await submitMut.mutateAsync({
        verificationToken: verification.verificationToken,
        consentVersion: linkQuery.data.consentVersion,
      })
      setStep('done')
    } catch (err) {
      const outcome = outcomeOf(err)
      if (outcome === 'copy_updated') {
        // Active copy changed between load and submit — pull the new version and re-gate.
        await linkQuery.refetch()
        setReviewNotice('This consent form was updated. Please review the new version before signing.')
      } else if (outcome === 'verification_invalid') {
        setVerification(null)
        setVerifyNotice('Your identity check expired. Please confirm your date of birth again.')
        setStep('verify')
      } else if (outcome === 'expired') {
        setTerminal('expired')
      }
      // 'not_accepted' / generic are shown inline (derived from submitMut.error below)
    }
  }

  // ── Render. Terminal screens take precedence, then load state, then the step. ──
  if (terminal) return <TerminalScreen reason={terminal} />
  if (linkQuery.isPending) return <LoadingCard />
  if (linkQuery.isError) return <TerminalScreen reason={loadTerminal(linkQuery.error)} />

  const link = linkQuery.data

  if (step === 'done') return <DoneScreen clinicName={link.clinicName} />

  if (step === 'review' && verification) {
    const submitError =
      submitMut.isError && outcomeOf(submitMut.error) === 'not_accepted'
        ? 'Please tick the box to agree before continuing.'
        : submitMut.isError && !['copy_updated', 'verification_invalid', 'expired'].includes(outcomeOf(submitMut.error))
          ? 'Something went wrong. Please try again.'
          : null
    return (
      <ReviewStep
        clinicName={link.clinicName}
        patientName={verification.patientName}
        body={link.body}
        videoReference={link.videoReference}
        consentCheckboxText={link.consentCheckboxText}
        notice={reviewNotice}
        error={submitError}
        pending={submitMut.isPending}
        onSubmit={handleSubmit}
      />
    )
  }

  const verifyError =
    verifyMut.isError && outcomeOf(verifyMut.error) === 'mismatch'
      ? "That date of birth doesn't match our records. Please check and try again."
      : verifyMut.isError && !['locked', 'rate_limited', 'expired'].includes(outcomeOf(verifyMut.error))
        ? 'Something went wrong. Please try again.'
        : null
  return (
    <VerifyStep
      clinicName={link.clinicName}
      notice={verifyNotice}
      error={verifyError}
      pending={verifyMut.isPending}
      onSubmit={handleVerify}
    />
  )
}

/* ── Step 1: confirm date of birth ──────────────────────────────── */
function VerifyStep({
  clinicName,
  notice,
  error,
  pending,
  onSubmit,
}: {
  clinicName: string
  notice: string | null
  error: string | null
  pending: boolean
  onSubmit: (dateOfBirth: string) => void
}) {
  const [dob, setDob] = useState('')
  const headingRef = useHeadingFocus()

  return (
    <Card>
      <CardHeader>
        <ClinicLabel>{clinicName}</ClinicLabel>
        <CardTitle ref={headingRef}>Confirm your identity</CardTitle>
        <CardDescription>
          To protect your privacy, please confirm your date of birth to view and sign your consent form.
        </CardDescription>
        <Stepper step={1} />
      </CardHeader>
      <CardContent>
        {notice && <Notice tone="warn">{notice}</Notice>}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (dob && !pending) onSubmit(dob)
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="dob">Date of birth</Label>
            <Input
              id="dob"
              type="date"
              value={dob}
              max={todayIso()}
              autoComplete="bday"
              required
              onChange={(e) => setDob(e.target.value)}
            />
          </div>
          {error && <Notice tone="error">{error}</Notice>}
          <Button type="submit" size="lg" className="mt-5 w-full" disabled={!dob || pending}>
            {pending && <Loader2 className="size-4 animate-spin" />}
            Continue
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/* ── Step 2: review the copy and sign ───────────────────────────── */
function ReviewStep({
  clinicName,
  patientName,
  body,
  videoReference,
  consentCheckboxText,
  notice,
  error,
  pending,
  onSubmit,
}: {
  clinicName: string
  patientName: string
  body: string
  videoReference: string
  consentCheckboxText: string
  notice: string | null
  error: string | null
  pending: boolean
  onSubmit: () => void
}) {
  const [checked, setChecked] = useState(false)
  const headingRef = useHeadingFocus()
  const cleanBody = useMemo(() => DOMPurify.sanitize(body), [body])
  const videoUrl = useMemo(() => youTubeEmbedUrl(videoReference), [videoReference])

  return (
    <Card>
      <CardHeader>
        <ClinicLabel>{clinicName}</ClinicLabel>
        <CardTitle ref={headingRef}>Hi {patientName}</CardTitle>
        <CardDescription>Please read the consent below and sign to continue your care.</CardDescription>
        <Stepper step={2} />
      </CardHeader>
      <CardContent>
        {notice && <Notice tone="warn">{notice}</Notice>}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (checked && !pending) onSubmit()
          }}
        >
          {videoUrl && (
            <div className="mt-1 mb-4 aspect-video overflow-hidden rounded-md border bg-black">
              <iframe
                src={videoUrl}
                title="Program explainer video"
                className="size-full"
                // The page is no-referrer; YouTube needs the origin to validate embedding (else error 153).
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          )}

          <div
            role="region"
            aria-label="Consent form text"
            className="bg-muted/40 text-foreground/90 mt-1 rounded-md border p-4 text-sm leading-relaxed [&_li]:mb-1 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: cleanBody }}
          />

          <p className="mt-4 text-sm">
            <span className="text-muted-foreground">Patient name:</span>{' '}
            <span className="font-semibold">{patientName}</span>
          </p>

          <label className="has-[:checked]:border-primary has-[:checked]:bg-primary/5 mt-3 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="accent-primary mt-0.5 size-5 shrink-0"
            />
            <span className="text-sm">{consentCheckboxText}</span>
          </label>

          {error && <Notice tone="error">{error}</Notice>}

          <Button type="submit" size="lg" className="mt-5 w-full" disabled={!checked || pending}>
            {pending && <Loader2 className="size-4 animate-spin" />}
            Agree &amp; sign
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/* ── Terminal screens ───────────────────────────────────────────── */
function DoneScreen({ clinicName }: { clinicName: string }) {
  return (
    <StatusCard tone="success" icon={<CheckCircle2 className="size-7" />} title="Consent recorded">
      Thank you. Your consent for <strong>{clinicName}</strong> has been recorded. You can now close
      this page.
    </StatusCard>
  )
}

function TerminalScreen({ reason }: { reason: Terminal }) {
  const reload = (
    <Button variant="outline" className="mt-2" onClick={() => window.location.reload()}>
      Reload page
    </Button>
  )
  switch (reason) {
    case 'expired':
      return (
        <StatusCard tone="neutral" icon={<Clock className="size-7" />} title="This link is no longer active">
          For your security, consent links expire. Please contact your care team for a new link.
        </StatusCard>
      )
    case 'rate_limited':
      return (
        <StatusCard tone="neutral" icon={<Clock className="size-7" />} title="Too many requests" action={reload}>
          Please wait a minute, then reload this page to try again.
        </StatusCard>
      )
    case 'locked':
      return (
        <StatusCard tone="alert" icon={<ShieldAlert className="size-7" />} title="This link has been locked">
          For your protection, this link was locked after several incorrect attempts. Please contact
          your care team.
        </StatusCard>
      )
    default:
      return (
        <StatusCard tone="alert" icon={<TriangleAlert className="size-7" />} title="Something went wrong" action={reload}>
          We couldn't load your consent form. Please reload the page and try again.
        </StatusCard>
      )
  }
}

function LoadingCard() {
  return (
    <Card>
      <CardContent className="flex items-center justify-center py-16">
        <Loader2 className="text-muted-foreground size-7 animate-spin" />
        <span className="sr-only">Loading your consent form</span>
      </CardContent>
    </Card>
  )
}

/* ── Small shared bits ──────────────────────────────────────────── */
function ClinicLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{children}</p>
  )
}

function Stepper({ step }: { step: 1 | 2 }) {
  return (
    <ol className="mt-3 flex gap-2" aria-hidden="true">
      <li className="border-primary text-primary flex-1 border-t-2 pt-2 text-xs font-medium">1 · Verify</li>
      <li
        className={cn(
          'flex-1 border-t-2 pt-2 text-xs font-medium',
          step >= 2 ? 'border-primary text-primary' : 'border-border text-muted-foreground',
        )}
      >
        2 · Review &amp; sign
      </li>
    </ol>
  )
}

function Notice({ tone, children }: { tone: 'warn' | 'error'; children: ReactNode }) {
  return (
    <div
      role="alert"
      className={cn(
        'mt-4 rounded-md border px-3 py-2 text-sm',
        tone === 'error'
          ? 'border-destructive/30 bg-destructive/5 text-destructive'
          : 'border-amber-300/70 bg-amber-50 text-amber-800',
      )}
    >
      {children}
    </div>
  )
}

/** Move focus to the step heading on mount so screen-reader users hear the new step. */
function useHeadingFocus() {
  const ref = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    ref.current?.focus()
  }, [])
  return ref
}
